/**
 * Video slider - 3 slide "material" style carousel.
 *
 * The slider always shows the active slide (wide), the next slide (narrower)
 * and every following slide (narrowest). Navigating shifts everything by one
 * slide and re-computes each slide position/width from scratch, so the last
 * slide can always become active and sit flush on the left edge.
 */

const ACTIVE_SLIDE_RATIO = 0.56;
const NEXT_SLIDE_RATIO = 0.28;
const THIRD_SLIDE_RATIO = 0.16;
const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_ACTIVE_SLIDE_RATIO = 1 / 1.05; // ~1.05 slides visible.
const MOBILE_NEXT_SLIDE_RATIO = 1 - MOBILE_ACTIVE_SLIDE_RATIO;
const MOBILE_THIRD_SLIDE_RATIO = MOBILE_NEXT_SLIDE_RATIO;
const SLIDE_GAP_PX = 16;
const MOBILE_SLIDE_GAP_PX = 8; // 0.5rem.
const SWIPE_THRESHOLD_PX = 40;
const RESIZE_DEBOUNCE_MS = 150;

/**
 * Returns the width ratio for a slide based on its position relative to the active slide.
 *
 * @param {number}  relativeIndex Slide index minus the active index (0 = active, 1 = next slide).
 * @param {boolean} isMobile      Whether the mobile (narrow viewport) ratio set should be used.
 *
 * @return {number} Width ratio to apply to the slide.
 */
const getSlideRatio = ( relativeIndex, isMobile ) => {

	if ( relativeIndex === 0 ) {

		return isMobile ? MOBILE_ACTIVE_SLIDE_RATIO : ACTIVE_SLIDE_RATIO;

	} else if ( relativeIndex === 1 ) {

		return isMobile ? MOBILE_NEXT_SLIDE_RATIO : NEXT_SLIDE_RATIO;

	}

	return isMobile ? MOBILE_THIRD_SLIDE_RATIO : THIRD_SLIDE_RATIO;

};

/**
 * Controller for a single `.b-video-slider` block instance.
 */
class VideoSlider {

	/**
	 * Sets up references, builds the material slide markup and binds events.
	 *
	 * @param {HTMLElement} block Root `.b-video-slider` block element.
	 */
	constructor( block ) {

		this.block = block;
		this.sliderEl = block.querySelector( '.b-video-slider__slider' );
		this.wrapperEl = block.querySelector( '.b-video-slider__slides' );
		this.prevButton = block.querySelector( '.b-video-slider__button--prev' );
		this.nextButton = block.querySelector( '.b-video-slider__button--next' );
		this.activeIndex = 0;

		if ( ! this.sliderEl || ! this.wrapperEl ) {

			return;

		}

		this.wrapMaterialSlides();

		this.slides = Array.from( this.wrapperEl.querySelectorAll( '.b-video-slider__slide' ) );

		if ( this.slides.length < 1 ) {

			return;

		}

		this.bindEvents();
		this.layout();

	}

	/**
	 * Wraps every raw inner-block slide with the material slide/wrapper/content markup.
	 *
	 * @return {void}
	 */
	wrapMaterialSlides() {

		const initialSlideNodes = Array.from( this.wrapperEl.children ).filter(
			( childNode ) => childNode.nodeType === 1 && ! childNode.classList.contains( 'b-video-slider__slide' )
		);

		initialSlideNodes.forEach( ( slideNode ) => {

			const materialSlide = document.createElement( 'div' );
			const materialWrapper = document.createElement( 'div' );
			const materialContent = document.createElement( 'div' );
			const videoElement = slideNode.querySelector( 'video' );

			materialSlide.className = 'b-video-slider__slide';
			materialWrapper.className = 'swiper-material-wrapper';
			materialContent.className = 'swiper-material-content';

			if ( videoElement ) {

				videoElement.classList.add( 'b-video-slider__video' );
				videoElement.setAttribute( 'data-swiper-material-scale', '1.15' );
				videoElement.setAttribute( 'playsinline', '' );
				videoElement.setAttribute( 'preload', 'metadata' );

			}

			materialContent.appendChild( slideNode );
			materialWrapper.appendChild( materialContent );
			materialSlide.appendChild( materialWrapper );

			this.wrapperEl.appendChild( materialSlide );

		} );

	}

	/**
	 * Binds navigation button clicks, window resize and swipe gestures.
	 *
	 * @return {void}
	 */
	bindEvents() {

		if ( this.prevButton ) {

			this.prevButton.addEventListener( 'click', () => this.goTo( this.activeIndex - 1 ) );

		}

		if ( this.nextButton ) {

			this.nextButton.addEventListener( 'click', () => this.goTo( this.activeIndex + 1 ) );

		}

		let resizeTimeoutId;
		window.addEventListener( 'resize', () => {

			clearTimeout( resizeTimeoutId );
			resizeTimeoutId = setTimeout( () => this.layout(), RESIZE_DEBOUNCE_MS );

		} );

		this.bindSwipe();

	}

	/**
	 * Detects horizontal pointer swipes on the slides wrapper and navigates accordingly.
	 *
	 * @return {void}
	 */
	bindSwipe() {

		let startX = null;

		this.wrapperEl.addEventListener( 'pointerdown', ( event ) => {

			startX = event.clientX;

		} );

		this.wrapperEl.addEventListener( 'pointerup', ( event ) => {

			if ( startX === null ) {

				return;

			}

			const deltaX = event.clientX - startX;

			startX = null;

			if ( Math.abs( deltaX ) < SWIPE_THRESHOLD_PX ) {

				return;

			}

			this.goTo( this.activeIndex + ( deltaX < 0 ? 1 : -1 ) );

		} );

	}

	/**
	 * Navigates to the given slide index, clamped to the available range.
	 *
	 * @param {number} index Target slide index.
	 *
	 * @return {void}
	 */
	goTo( index ) {

		const clampedIndex = Math.min( Math.max( index, 0 ), this.slides.length - 1 );

		if ( clampedIndex === this.activeIndex ) {

			return;

		}

		this.activeIndex = clampedIndex;
		this.layout();

	}

	/**
	 * Computes and applies each slide's width and position based on the active index.
	 *
	 * @return {void}
	 */
	layout() {

		const isMobile = window.innerWidth <= MOBILE_BREAKPOINT_PX;
		const slideGap = isMobile ? MOBILE_SLIDE_GAP_PX : SLIDE_GAP_PX;
		const containerWidth = this.sliderEl.clientWidth - ( slideGap * 2 );
		const layoutData = [];
		let cumulativeOffset = 0;
		let activeOffset = 0;

		this.slides.forEach( ( slide, slideIndex ) => {

			const relativeIndex = slideIndex - this.activeIndex;
			const ratio = relativeIndex < 0 ? getSlideRatio( 2, isMobile ) : getSlideRatio( relativeIndex, isMobile );
			const widthPx = containerWidth * ratio;
			const isActive = relativeIndex === 0;

			layoutData.push( { slide, widthPx, offset: cumulativeOffset, isActive } );

			if ( isActive ) {

				activeOffset = cumulativeOffset;

			}

			cumulativeOffset += widthPx + slideGap;

		} );

		layoutData.forEach( ( { slide, widthPx, offset, isActive } ) => {

			slide.style.width = `${ widthPx }px`;
			slide.style.transform = `translate3d(${ offset - activeOffset }px, 0, 0)`;
			slide.classList.toggle( 'is-active', isActive );

			const opacityElements = slide.querySelectorAll( '.swiper-material-animate-opacity' );
			const scaleElements = slide.querySelectorAll( '[data-swiper-material-scale]' );

			opacityElements.forEach( ( element ) => {

				element.style.opacity = isActive ? '1' : '0';

			} );

			scaleElements.forEach( ( element ) => {

				const maxScale = parseFloat( element.getAttribute( 'data-swiper-material-scale' ) ) || 1;

				element.style.transform = isActive ? 'scale(1)' : `scale(${ maxScale })`;

			} );

		} );

		this.updateNavigation();

	}

	/**
	 * Enables/disables the prev/next buttons when the active slide is at either end.
	 *
	 * @return {void}
	 */
	updateNavigation() {

		if ( this.prevButton ) {

			this.prevButton.disabled = this.activeIndex === 0;

		}

		if ( this.nextButton ) {

			this.nextButton.disabled = this.activeIndex === this.slides.length - 1;

		}

	}

}

document.querySelectorAll( '.b-video-slider' ).forEach( ( block ) => new VideoSlider( block ) );
